// Esquema de "cantidad dinámica de documentos": el usuario define por tipo
// cuántos documentos emitir (1..N, N = ítems del tipo) y cada ítem queda
// asignado obligatoriamente a uno de esos documentos (round-robin
// determinista por descripción). El campo por ítem es `agrupamiento`
// (número de documento 1..cantidad; null/0 = sin asignar). El estado
// consolidado/separado ya no viaja desde el front: el backend lo infiere de
// la distribución (un documento con ≥ 2 ítems del tipo = consolidado; con 1
// = separado).

// Mapa {descripcion: cantidad} desde la configuración editable del box
export const cantidadPorTipo = (config) => {
  const mapa = {};
  (Array.isArray(config) ? config : []).forEach((c) => {
    const nombre = c.descripcion ?? c.documentoCobro;
    if (!nombre) return;
    const n = Number(c.cantidadDocumentos);
    mapa[nombre] = Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
  });
  return mapa;
};

// Re-distribuye round-robin los ítems: orden por descripción y agrupamiento
// = (posicion % cantidad) + 1 dentro de su tipo. Con `cobro` redistribuye
// solo ese tipo; sin él, todos los tipos con cantidad definida. Los ítems de
// tipos sin cantidad quedan intactos.
export const distribuirPorCantidad = (items, cantidades, cobro = null) => {
  const lista = Array.isArray(items) ? items : [];
  const mapa = cantidades && typeof cantidades === "object" ? cantidades : {};
  const agrupamientoPorId = new Map();
  const grupos = new Map();
  lista.forEach((item) => {
    if (cobro && item.cobro !== cobro) return;
    const cantidad = mapa[item.cobro];
    if (!Number.isFinite(cantidad) || cantidad < 1) return;
    const grupo = grupos.get(item.cobro) ?? [];
    grupo.push(item);
    grupos.set(item.cobro, grupo);
  });
  grupos.forEach((grupo) => {
    [...grupo]
      .sort((a, b) =>
        String(a.descripcion ?? "").localeCompare(String(b.descripcion ?? ""))
      )
      .forEach((item, idx) => {
        agrupamientoPorId.set(item.id, (idx % mapa[item.cobro]) + 1);
      });
  });
  return lista.map((item) =>
    agrupamientoPorId.has(item.id)
      ? { ...item, agrupamiento: agrupamientoPorId.get(item.id) }
      : item
  );
};

// Normaliza al abrir: conserva la distribución guardada cuando todos los
// ítems del tipo caen dentro del rango 1..cantidad; re-distribuye round-robin
// el tipo completo si alguno quedó fuera (valores legacy/obsoletos).
export const normalizarDocumentos = (items, cantidades) => {
  const lista = Array.isArray(items) ? items : [];
  const mapa = cantidades && typeof cantidades === "object" ? cantidades : {};
  const obsoletos = new Set();
  lista.forEach((item) => {
    const cantidad = mapa[item.cobro];
    if (!Number.isFinite(cantidad) || cantidad < 1) return;
    const doc = Number(item.agrupamiento);
    if (!(doc >= 1 && doc <= cantidad)) obsoletos.add(item.cobro);
  });
  let resultado = lista;
  obsoletos.forEach((cobro) => {
    resultado = distribuirPorCantidad(resultado, mapa, cobro);
  });
  return resultado;
};

// Valida el esquema antes de guardar. Para cada tipo gestionado (con cantidad
// definida en el box):
// 1. Cada ítem debe estar asignado a un documento dentro del rango declarado
//    (1..cantidad).
// 2. Cada documento declarado debe tener al menos 1 ítem: la cantidad elegida
//    debe coincidir con la distribución real (con el round-robin automático
//    esto se cumple por construcción; dispara cuando el usuario mueve ítems
//    manualmente y deja documentos vacíos).
// Los ítems de tipos sin cantidad definida no se gestionan (emiten en
// documento propio). Devuelve el mensaje de error o null.
export const validarAsignacionDocumentos = (items, cantidades) => {
  const lista = Array.isArray(items) ? items : [];
  const mapa = cantidades && typeof cantidades === "object" ? cantidades : {};
  const gestionados = lista.filter((item) => {
    const cantidad = mapa[item.cobro];
    return Number.isFinite(cantidad) && cantidad >= 1;
  });
  const fueraDeRango = gestionados.find((item) => {
    const doc = Number(item.agrupamiento);
    return !(doc >= 1 && doc <= mapa[item.cobro]);
  });
  if (fueraDeRango) {
    const doc = Number(fueraDeRango.agrupamiento);
    return `Cada ítem debe estar asignado a uno de los ${mapa[fueraDeRango.cobro]} documentos del tipo "${fueraDeRango.cobro}" (hay un ítem con el documento ${doc >= 1 ? doc : "sin asignar"}).`;
  }
  // Completitud: la cantidad declarada debe coincidir con los documentos
  // realmente usados en la asignación
  const usados = new Map();
  gestionados.forEach((item) => {
    const docs = usados.get(item.cobro) ?? new Set();
    docs.add(Number(item.agrupamiento));
    usados.set(item.cobro, docs);
  });
  for (const [cobro, docs] of usados) {
    const faltantes = [];
    for (let d = 1; d <= mapa[cobro]; d++) {
      if (!docs.has(d)) faltantes.push(d);
    }
    if (faltantes.length > 0) {
      return `La cantidad definida para "${cobro}" (${mapa[cobro]}) no coincide con la asignación: ${
        faltantes.length === 1 ? "el documento" : "los documentos"
      } ${faltantes.join(", ")} ${
        faltantes.length === 1 ? "quedó" : "quedaron"
      } sin ítems.`;
    }
  }
  return null;
};